/** Small shared pieces: empty state, alert, tabs, skeleton, page header, money and date text. */
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatDate, formatDateTime, formatMoney, type DateInput, type MoneyLike } from '../format';
import { cn } from '../lib/cn';

/** Every screen ships an empty state (docs/09 "Screens every module ships"). */
export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div data-empty className={cn('flex flex-col items-center gap-2 px-4 py-10 text-center', className)}>
      {Icon ? <Icon aria-hidden className="size-6 text-muted-foreground" strokeWidth={2} /> : null}
      <p className="font-display text-base font-semibold">{title}</p>
      {body ? <p className="max-w-md text-sm text-muted-foreground">{body}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
  action,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const color = {
    info: 'var(--oex-cyan)',
    warning: 'var(--oex-amber)',
    danger: 'hsl(var(--destructive))',
    success: 'var(--oex-lime)',
  }[tone];
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex flex-wrap items-center gap-3 rounded-[4px] border px-3 py-2 text-sm', className)}
      style={{ borderColor: color, backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)` }}
    >
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="text-foreground/90">{children}</div> : null}
      </div>
      {action}
    </div>
  );
}

export type TabItem<K extends string> = { key: K; label: ReactNode; count?: number };

export function Tabs<K extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem<K>[];
  value: K;
  onChange(key: K): void;
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-1 border-b border-border">
      {items.map((item) => (
        <button
          key={item.key}
          role="tab"
          type="button"
          aria-selected={item.key === value}
          onClick={() => onChange(item.key)}
          className={cn(
            '-mb-px border-b-2 px-3 py-2 text-sm transition-colors signal-focus',
            item.key === value
              ? 'border-[var(--oex-purple)] font-semibold text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {item.label}
          {item.count !== undefined ? <span className="font-data ml-1.5 text-xs">({item.count})</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-[4px] bg-muted', className)} />;
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

/** Money: mono, currency code after the amount, negatives in parentheses when `finance`. */
export function Money({
  value,
  finance,
  className,
}: {
  value: MoneyLike;
  finance?: boolean;
  className?: string;
}) {
  return (
    <span className={cn('font-data tabular-nums', className)}>
      {formatMoney(value, { parensNegative: finance })}
    </span>
  );
}

export function DateText({
  value,
  withTime,
  timeZone,
  className,
}: {
  value: DateInput;
  withTime?: boolean;
  timeZone?: string;
  className?: string;
}) {
  const d = value ? new Date(value) : null;
  return (
    <time dateTime={d ? d.toISOString() : undefined} className={cn('font-data', className)}>
      {withTime
        ? formatDateTime(value, timeZone ? { timeZone } : {})
        : formatDate(value, timeZone ? { timeZone } : {})}
    </time>
  );
}
